import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./CartPage.css";
import { getCartUserId } from "../utils/cartUser";
import {
  getLocalCartItems,
  isLocalCartItemId,
  removeItemFromLocalCart,
} from "../utils/localCart";

const parsePrice = (value) =>
  parseFloat(String(value ?? "").replace(/[^0-9.]/g, "")) || 0;

export default function CartPage() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const userId = getCartUserId();

  const fetchCart = useCallback(() => {
    setLoading(true);
    const localItems = getLocalCartItems(userId);

    fetch(`https://gp-live-backend.vercel.app/api/cart/${userId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const serverItems = Array.isArray(data) ? data : [];
        setItems([...serverItems, ...localItems]);
        setError("");
      })
      .catch(() => {
        setItems(localItems);
        setError(localItems.length === 0 ? "Failed to load cart" : "");
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleDelete = async (itemId) => {
    if (isLocalCartItemId(itemId)) {
      removeItemFromLocalCart(userId, itemId);
      setItems((prev) => prev.filter((item) => item._id !== itemId));
      window.dispatchEvent(new Event("cartUpdated"));
      return;
    }

    try {
      const res = await fetch(
        `https://gp-live-backend.vercel.app/api/cart/${userId}/${itemId}`,
        { method: "DELETE" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      setItems((prev) => prev.filter((item) => item._id !== itemId));
      window.dispatchEvent(new Event("cartUpdated"));
    } catch {
      // Keep UX smooth even if backend delete fails for synced/local mismatch.
      removeItemFromLocalCart(userId, itemId);
      setItems((prev) => prev.filter((item) => item._id !== itemId));
      window.dispatchEvent(new Event("cartUpdated"));
    }
  };

  const total = items.reduce((sum, item) => {
    return sum + parsePrice(item.price) * (item.quantity || 1);
  }, 0);

  const goToOrderConfirm = () => {
    if (items.length === 0) {
      alert("Cart is empty.");
      return;
    }
    navigate("/order-confirm");
  };

  return (
    <div className="cart-page">
      <h2>Your Cart</h2>
      <button className="back-btn" onClick={() => navigate("/e-store")}>
        Back to Store
      </button>

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : items.length === 0 ? (
        <p>No items in cart yet.</p>
      ) : (
        <>
          <div className="cart-items">
            {items.map((item) => {
              const subtotal = parsePrice(item.price) * (item.quantity || 1);

              return (
                <div key={item._id} className="cart-item">
                  <img src={item.img} alt={item.name} />
                  <div className="cart-info">
                    <h4>{item.name}</h4>
                    <p>Size: {item.size}</p>
                    <p>
                      Color: {item.color?.name || "N/A"} {item.color?.hex || "#000"}
                    </p>
                    <p>
                      Rs {item.price} x {item.quantity}
                    </p>
                    <p>
                      <strong>Subtotal: Rs {subtotal.toFixed(2)}</strong>
                    </p>
                  </div>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(item._id)}
                  >
                    Delete
                  </button>
                </div>
              );
            })}
          </div>

          <div className="cart-total">
            <h3>Total: Rs {total.toFixed(2)}</h3>
            <div className="cart-buttons">
              <button className="continue-btn" onClick={() => navigate("/e-store")}>
                Continue Shopping
              </button>
              <button className="buy-now-btn" onClick={goToOrderConfirm}>
                Buy Now
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
